/**
 * IS A NEWER BUILD ON THE SERVER — the version arithmetic behind the update
 * banner, kept apart from the component that draws it.
 *
 * WHY ITS OWN MODULE. The banner (src/components/UpdateBanner.tsx) is a screen —
 * a timer, a fetch, an animation — none of which a unit test can honestly reach.
 * The decision of whether a served build is worth interrupting somebody for is
 * pure, so it lives here and is tested on its own (test-telling-people-about-an-
 * update.ts). This was ANewVersion's internal logic until the update offer and
 * the What's-new tour were merged into one top banner (3.20.0); the arithmetic
 * did not change, only where it sits.
 */

/** Every few minutes is often enough for something nobody is waiting for. */
export const EVERY = 5 * 60 * 1000;
/** And not again within this of the last ask, however many times a tab is switched. */
export const NO_SOONER_THAN = 60 * 1000;

export const versionOf = (stamp: string) => stamp.split('|')[1] ?? '';
export const envOf = (stamp: string) => stamp.split('|')[3] ?? '';

/**
 * A VERSION AS NUMBERS, or nothing if it cannot be read as one.
 *
 * Both lines this app runs on produce a number this can read: a release is
 * `2.7.0`, and everything else is `1.42.0+614` — the same three parts with a
 * count of commits after them. The `+N` is the fourth number, which is what
 * makes two test-copy builds of the same release comparable at all.
 *
 * Anything with more than that in it — a `-dev` or an `-rc1` — is read as its
 * numbers and then refused below, because a suffix means something this cannot
 * order and guessing is how a downgrade gets offered.
 */
function asNumbers(version: string): number[] | null {
  const m = /^(\d+)\.(\d+)\.(\d+)(?:\+(\d+))?$/.exec(version.trim());
  return m ? [Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4] ?? 0)] : null;
}

/**
 * IS THE SERVED ONE ACTUALLY NEWER, rather than merely different.
 *
 * WHY THIS IS NOT "ARE THEY DIFFERENT", WHICH IS WHAT IT WAS. The witness
 * pointed `build.json` at `0.0.1` while the page ran `1.42.0+615` and was told
 * "A new version is ready". Different-and-same-copy is exactly the shape a
 * ROLLBACK takes: live goes back to yesterday's build, and everybody still on
 * today's is offered a downgrade described as something new. The reload does
 * what it says, so it is not a lie about the consequence — but the words are
 * untrue, and being told an older copy is new is how somebody loses faith in
 * every notice after it.
 *
 * UNREADABLE MEANS NO. A version this cannot order might be older, and there is
 * no harm in staying quiet — the page still works, and the next release with a
 * number in it will offer itself.
 */
function isNewer(mine: string, served: string): boolean {
  const a = asNumbers(mine);
  const b = asNumbers(served);
  if (!a || !b) return false;
  for (let i = 0; i < 4; i++) {
    if (b[i] !== a[i]) return b[i] > a[i];
  }
  return false;
}

/**
 * WHETHER WHAT THE SERVER IS SERVING IS SOMETHING TO OFFER.
 *
 * Its own function because every case below is a way of being wrong that costs
 * somebody a reload they did not need, and none of them can be checked through
 * a timer and a fetch.
 */
export function isWorthOffering(mine: string, served: string | null): boolean {
  if (!served || !mine) return false;
  if (!served.startsWith('flightdeck-build|')) return false;
  if (served === mine) return false;
  /*
   * A DIFFERENT COPY IS NOT A NEWER VERSION. Somebody with the test copy open
   * beside live, or a preview channel, is looking at two builds that differ in
   * every field — and offering to "update" one to the other would be wrong in
   * both directions.
   */
  if (envOf(served) !== envOf(mine)) return false;
  /*
   * AND NEITHER IS THE SAME VERSION BUILT AGAIN. The stamp carries the commit,
   * so any redeploy changes it; the number people quote is what makes it an
   * update worth interrupting somebody for.
   */
  if (versionOf(served) === versionOf(mine)) return false;
  /* Different is not enough — going back is different too. See isNewer. */
  return isNewer(versionOf(mine), versionOf(served));
}

/**
 * WHAT THE SERVER IS SERVING NOW, or nothing at all.
 *
 * Every failure is silence on purpose. This runs on a timer nobody asked for,
 * against a network that goes away — a laptop that closed, a tunnel, a deploy
 * in progress. None of that is worth a word on screen, and an offer to update
 * that appears because a fetch failed would be worse than not asking.
 */
export async function whatIsServed(): Promise<string | null> {
  try {
    const answer = await fetch('/build.json', { cache: 'no-store' });
    if (!answer.ok) return null;
    const said: unknown = await answer.json();
    const build = (said as { build?: unknown })?.build;
    return typeof build === 'string' && build.startsWith('flightdeck-build|') ? build : null;
  } catch {
    return null;
  }
}
